<script>
import { mapState, mapActions } from 'vuex'
import VueApexCharts from 'vue-apexcharts'

export default {
   components: {
      VueApexCharts,
   },
   data() {
      return {
         // b-tabs menggunakan index numerik sebagai nilai v-model.
         // Default tab mengikuti triwulan yang sedang berjalan (real time):
         // Jan-Mar -> Triwulan I, Apr-Jun -> II, Jul-Sep -> III, Okt-Des -> IV.
         activeTab: Math.floor(new Date().getMonth() / 3),
         loading: false,
      }
   },
   computed: {
      ...mapState({
         chartResponse: state => state.DashboardSkm.perInstansiChart,
         tahun: state => state.DashboardSkm.tahun,
         refreshTimestamp: state => state.DashboardSkm.refreshTimestamp,
         skin: state => state.appConfig.layout.skin,
      }),
      isDark() {
         return this.skin === 'dark'
      },
      activeTw() {
         return this.activeTab + 1
      },
      // ApexCharts menggambar kategori horizontal bar dari atas ke bawah,
      // sehingga dengan urutan menurun indeks terbaik selalu berada paling atas.
      sortedData() {
         return (this.chartResponse.data || [])
            .map(item => ({
               ...item,
               value: Number(String((item || {}).indeks || 0).replace(',', '.')) || 0,
            }))
            .sort((a, b) => b.value - a.value)
      },
      chartSeries() {
         return [{
            name: 'Indeks SKM',
            data: this.sortedData.map(item => item.value),
         }]
      },
      chartOptions() {
         const textColor = this.isDark ? '#b4b7bd' : '#6e6b7b'

         return {
            chart: {
               type: 'bar',
               foreColor: textColor,
               toolbar: {
                  show: false,
               },
               fontFamily: 'Montserrat, Helvetica, Arial, sans-serif',
            },
            colors: ['#00cfe8'],
            plotOptions: {
               bar: {
                  horizontal: true,
                  borderRadius: 4,
                  barHeight: '60%',
                  endingShape: 'rounded',
               },
            },
            dataLabels: {
               enabled: true,
               textAnchor: 'start',
               offsetX: 0,
               formatter: value => value.toFixed(2).replace('.', ','),
               style: {
                  colors: [textColor],
               },
            },
            grid: {
               borderColor: this.isDark ? '#3b4253' : '#ebe9f1',
               strokeDashArray: 4,
            },
            // Horizontal bar: nama instansi (kategori) di sumbu vertikal,
            // nilai indeks pada sumbu nilai dengan skala 0-100.
            xaxis: {
               categories: this.sortedData.map(item => item.instansi),
               min: 0,
               max: 100,
               title: {
                  text: 'Indeks SKM',
                  style: {
                     color: textColor,
                  },
               },
               labels: {
                  style: {
                     colors: textColor,
                  },
                  formatter: value => (typeof value === 'number' ? `${Math.round(value)}` : value),
               },
            },
            yaxis: {
               labels: {
                  style: {
                     colors: textColor,
                  },
               },
            },
            tooltip: {
               theme: this.isDark ? 'dark' : 'light',
               y: {
                  formatter: (value, opts) => {
                     const item = this.sortedData[opts.dataPointIndex] || {}
                     const responden = Number(item.responden || 0).toLocaleString('id-ID')

                     return `Indeks ${item.indeks || '0,00'} (${item.grade || '-'}) • ${responden} responden`
                  },
               },
            },
         }
      },
   },
   watch: {
      tahun(newYear, oldYear) {
         if (newYear !== oldYear) this.loadData({ year: newYear, tw: this.activeTw })
      },
      activeTab() {
         this.loadData({ year: this.tahun, tw: this.activeTw })
      },
      refreshTimestamp() {
         this.loadData({ year: this.tahun, tw: this.activeTw })
      },
   },
   mounted() {
      this.loadData({ year: this.tahun, tw: this.activeTw })
   },
   methods: {
      ...mapActions({
         getPerInstansiChart: 'DashboardSkm/getPerInstansiChart',
      }),
      async loadData(params) {
         this.loading = true
         try {
            await this.getPerInstansiChart(params)
         } catch (error) {
            // Chart tetap menampilkan data kosong saat request gagal.
         } finally {
            this.loading = false
         }
      },
   },
}
</script>

<template>
   <b-overlay
      :show="loading"
      :variant="isDark ? 'dark' : 'white'"
      spinner-variant="primary"
      blur="0"
      opacity=".75"
      rounded="sm"
   >
      <b-row>
         <b-col cols="12">
            <b-card
               class="card-wrapper"
               :class="{ 'card-wrapper-dark': isDark }"
            >
               <div class="d-flex justify-content-between align-items-center mb-1">
                  <div>
                     <h4 class="mb-25">
                        IKM per Instansi
                     </h4>
                     <small class="text-muted">Indeks SKM setiap instansi Tahun {{ tahun }}</small>
                  </div>
               </div>

               <div class="mb-1">
                  <b-tabs v-model="activeTab" pills>
                     <b-tab title="Triwulan I" />
                     <b-tab title="Triwulan II" />
                     <b-tab title="Triwulan III" />
                     <b-tab title="Triwulan IV" />
                  </b-tabs>
               </div>

               <vue-apex-charts
                  type="bar"
                  height="500"
                  :options="chartOptions"
                  :series="chartSeries"
               />
            </b-card>
         </b-col>
      </b-row>
   </b-overlay>
</template>

<style scoped>
.card-wrapper {
   border: 0;
   border-radius: 12px;
   box-shadow: 0 4px 24px rgba(34, 41, 47, 0.08);
   background-color: #ffffff;
}

.card-wrapper-dark {
   background-color: #283046;
   box-shadow: 0 4px 24px rgba(0, 0, 0, 0.25);
}
</style>
